import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useLanguage } from '../contexts/LanguageContext';

const HeroContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)'
    : 'linear-gradient(135deg, #F9FAFB 0%, #E5E7EB 50%, #F9FAFB 100%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'url("https://images.unsplash.com/photo-1648798402842-491733124f1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHw0fHx0ZWNobm9sb2d5JTIwYWJzdHJhY3QlMjBuZXR3b3JrJTIwZGlnaXRhbHxlbnwwfDB8fGJsdWV8MTc1OTI3NDg1MXww&ixlib=rb-4.1.0&q=85")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: theme.palette.mode === 'dark' ? 0.25 : 0.15,
    zIndex: 0,
  },
}));

const FloatingShape = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}40 0%, ${theme.palette.secondary.main}40 100%)`,
  filter: 'blur(80px)',
  animation: 'float 20s ease-in-out infinite',
  '@keyframes float': {
    '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
    '33%': { transform: 'translate(30px, -30px) scale(1.1)' },
    '66%': { transform: 'translate(-20px, 20px) scale(0.9)' },
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: '#FFFFFF',
  padding: '14px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  boxShadow: '0 8px 24px rgba(37, 99, 235, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 32px rgba(37, 99, 235, 0.4)',
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
  },
}));

const OutlineButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  padding: '14px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  border: `2px solid ${theme.palette.primary.main}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    background: theme.palette.mode === 'dark'
      ? 'rgba(37, 99, 235, 0.1)'
      : 'rgba(37, 99, 235, 0.05)',
    borderColor: theme.palette.primary.dark,
  },
}));

export default function HeroSection() {
  const { t } = useLanguage();

  return (
    <HeroContainer component="section" id="home" aria-label="Hero section">
      {/* Floating Background Shapes */}
      <FloatingShape sx={{ width: 400, height: 400, top: '10%', left: '5%' }} />
      <FloatingShape sx={{ width: 300, height: 300, bottom: '15%', right: '10%', animationDelay: '-5s' }} />
      <FloatingShape sx={{ width: 250, height: 250, top: '50%', right: '20%', animationDelay: '-10s' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: 12 }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem', lg: '4.5rem' },
              fontWeight: 800,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 2,
              animation: 'fadeInUp 1s ease-out',
              '@keyframes fadeInUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(30px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {t('hero.title')}
          </Typography>

          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              maxWidth: 800,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              lineHeight: 1.8,
              animation: 'fadeInUp 1s ease-out 0.2s both',
            }}
          >
            {t('hero.subtitle')}
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              mt: 4,
              animation: 'fadeInUp 1s ease-out 0.4s both',
            }}
          >
            <GradientButton
              endIcon={<ArrowForwardIcon />}
              href="#services"
            >
              {t('hero.cta.primary')}
            </GradientButton>

            <OutlineButton href="#contact">
              {t('hero.cta.secondary')}
            </OutlineButton>
          </Stack>

          {/* Stats */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={4}
            sx={{
              mt: 8,
              animation: 'fadeInUp 1s ease-out 0.6s both',
            }}
          >
            {[
              { value: '15+', label: t('why.experience.title') },
              { value: '200+', label: t('why.projects.title') },
              { value: '150+', label: t('why.clients.title') },
            ].map((stat, index) => (
              <Box key={index} sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Container>
    </HeroContainer>
  );
}